import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import styles from "./ConsentTemplatesPage.module.scss";
import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";
import { FiEdit2 } from "react-icons/fi";
import { toast } from "react-toastify";
import ImageGalleryV2 from "../../ImageGallery/imageGalleryV3";
import { HiSparkles } from "react-icons/hi2";

const STEPS: Step[] = [
  { key: "practice", label: "Practice and personal info", path: "/practiceDetails" },
  { key: "logo", label: "Logo Management", path: "/logoManagement" },
  { key: "social", label: "Social Media Links", path: "/socialLinks" },
  { key: "portfolio", label: "Portfolio Gallery", path: "/portfolioGallery" },
  { key: "consent", label: "Consent Templates", path: "/consentTemplates" },
  { key: "preview", label: "Preview & Complete", path: "/previewComplete" },
];

type Template = {
  id: number;
  name: string;
  body: string;
  level?: "user" | "practice";
  created_at?: string;
  is_default?: boolean;
  is_cloned?: boolean;
  parent_id?: number | null;
  category_id?: number | null;
  treatment_id?: number | null;
  treatment_ids?: number[];
};

type Treatment = {
  id: number;
  category_type?: string;
  category_name?: string;
  description?: string;
  name?: string;
};

declare global {
  interface Window {
    CKEDITOR: any;
  }
}

export default function ConsentTemplatesPage() {
  const navigate = useNavigate();
  const editorRef = useRef<HTMLTextAreaElement | null>(null);

  const [mode, setMode] = useState<"empty" | "builder">("empty");
  const [builderTab, setBuilderTab] = useState<"existing" | "generate">("existing");

  const [templates, setTemplates] = useState<Template[]>([]);
  const [practiceTemplates, setPracticeTemplates] = useState<Template[]>([]);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [loading, setLoading] = useState(false);

  const [preview, setPreview] = useState<Template | null>(null);
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [user, setUser] = useState<any>(null);

  const [selectedLevel, setSelectedLevel] = useState<"user" | "practice">("user");
  const [selectedTreatments, setSelectedTreatments] = useState<number[]>([]);
  const [treatmentSearch, setTreatmentSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [showGallery, setShowGallery] = useState(false);
  const [activeEditorId, setActiveEditorId] = useState("editor1");

  const [generatorPrompt, setGeneratorPrompt] = useState("");
  const [generatingTemplate, setGeneratingTemplate] = useState(false);

  const getToken = () =>
    axios.defaults.headers.common.Authorization?.replace("Bearer ", "") ||
    localStorage.getItem("token") ||
    Cookies.get("token");

  const getTreatmentLabel = (t: Treatment) =>
    t.description || t.name || t.category_name || `Treatment ${t.id}`;

  const countWords = (text: string) =>
    text.trim().split(/\s+/).filter(Boolean).length;

  useEffect(() => {
    const handler = (e: any) => {
      const editorId = e?.detail?.editorId || "editor1";
      setActiveEditorId(editorId);
      setShowGallery(true);
    };

    window.addEventListener("ckeditor-open-gallery", handler as EventListener);
    return () => window.removeEventListener("ckeditor-open-gallery", handler as EventListener);
  }, []);

  const uploadCallback = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    return axios.post("/uploads/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get("/user");
        if (Array.isArray(data) && data.length > 1) {
          setUser(data[1]);
        } else {
          setUser(data);
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (!user?.level) return;
    setSelectedLevel(user.level === "practice" ? "practice" : "user");
  }, [user?.level]);

  useEffect(() => {
    const fetchTreatments = async () => {
      try {
        const token = getToken();

        const res = await axios.get(`${process.env.REACT_APP_BASE_URL}treatment`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        setTreatments(res.data?.treatments || []);
      } catch (err) {
        console.error("Failed to load treatments:", err);
        setTreatments([]);
      }
    };

    fetchTreatments();
  }, []);

  const fetchPracticeTemplates = async () => {
    setLoading(true);
    try {
      const token = getToken();

      const endpoint =
        user?.level === "practice"
          ? `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practice_consent_templates`
          : `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practitioner_consent_templates`;

      const res = await axios.get(endpoint, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setPracticeTemplates(res.data || []);
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.level) {
      fetchPracticeTemplates();
    }
  }, [user?.level]);

  useEffect(() => {
    const fetchGlobalTemplates = async () => {
      if (mode !== "builder" || builderTab !== "existing") return;

      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_global_consent_templates`,
          {
            headers: {
              Accept: "application/json",
            },
          }
        );

        setTemplates(res.data || []);
      } catch (err) {
        console.error("Failed to load global templates:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchGlobalTemplates();
  }, [mode, builderTab]);

  useEffect(() => {
    if (!editTemplate) return;

    const destroyAllEditors = () => {
      const instances = window.CKEDITOR?.instances || {};
      Object.keys(instances).forEach((key) => {
        try {
          instances[key].destroy(true);
        } catch (err) {
          console.error(`Failed to destroy CKEditor instance: ${key}`, err);
        }
      });
    };

    const loadEditor = () => {
      if (!editorRef.current) return;

      editorRef.current.id = "editor1";
      destroyAllEditors();

      const editor = window.CKEDITOR.replace("editor1", {
        height: 450,
        extraPlugins:
          "placeholder_select,simpleImageUpload,image2,image,signaturebuttons,gallerybutton,uploadimage",
        toolbar: [
          { name: "basicstyles", items: ["Bold", "Italic", "Underline", "Strike"] },
          {
            name: "paragraph",
            items: ["NumberedList", "BulletedList", "Outdent", "Indent", "Blockquote"],
          },
          { name: "links", items: ["Link", "Unlink"] },
          { name: "styles", items: ["Format", "Font", "FontSize"] },
          { name: "colors", items: ["TextColor", "BGColor"] },
          {
            name: "insert",
            items: ["GalleryButton", "SignatureMandatory", "SignatureOptional", "ClinicianSignature"],
          },
        ],
        allowedContent: true,
        image2_disableResizer: false,
        image_prefillDimensions: true,
      });

      editor.on("instanceReady", () => {
        editor.setData(editTemplate.body || "");
      });

      setActiveEditorId(editor.name || "editor1");
    };

    if (!window.CKEDITOR) {
      const existingScript = document.querySelector('script[src="/ckeditor/ckeditor.js"]');
      if (!existingScript) {
        const script = document.createElement("script");
        script.src = "/ckeditor/ckeditor.js";
        script.onload = loadEditor;
        document.body.appendChild(script);
      } else {
        loadEditor();
      }
    } else {
      loadEditor();
    }

    return () => {
      destroyAllEditors();
    };
  }, [editTemplate?.id]);

  const openGlobalTemplateEditor = (template: Template) => {
    setEditTemplate({
      ...template,
      treatment_ids: [],
    });
    setSelectedTreatments([]);
    setSelectedLevel(user?.level === "practice" ? "practice" : "user");
  };

  const handleEditPracticeTemplate = (template: Template) => {
    setEditTemplate({
      ...template,
    });

    setSelectedLevel(template.level || (user?.level === "practice" ? "practice" : "user"));
    setSelectedTreatments(template.treatment_ids || []);
    setMode("builder");
    setBuilderTab("generate");
  };

  const handleGenerateTemplate = async () => {
    const trimmedPrompt = generatorPrompt.trim();
    const wordCount = countWords(trimmedPrompt);

    if (!trimmedPrompt) {
      toast.error("Please enter a treatment prompt.");
      return;
    }

    if (wordCount > 20) {
      toast.error("Prompt must be 20 words or fewer.");
      return;
    }

    try {
      setGeneratingTemplate(true);

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL_V2}template_generator/generate-template`,
        {
          treatment_item: trimmedPrompt,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const generatedHtml =
        res.data?.html ||
        res.data?.body ||
        res.data?.template ||
        res.data?.data?.html ||
        res.data;

      if (typeof generatedHtml !== "string" || !generatedHtml.trim()) {
        throw new Error("Template generator returned an invalid HTML response.");
      }

      setSelectedTreatments([]);
      setTreatmentSearch("");
      setDropdownOpen(false);

      setEditTemplate({
        id: 0,
        name: trimmedPrompt,
        body: generatedHtml,
        level: user?.level === "practice" ? "practice" : "user",
        treatment_ids: [],
      });

      toast.success("Template generated successfully");
    } catch (err: any) {
      console.error(err);

      const backendMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to generate template";

      toast.error(
        typeof backendMessage === "string"
          ? backendMessage
          : JSON.stringify(backendMessage)
      );
    } finally {
      setGeneratingTemplate(false);
    }
  };

  const handleSave = async () => {
    if (!editTemplate) return;

    try {
      const editorContent =
        window.CKEDITOR?.instances?.editor1?.getData?.() ?? editTemplate.body ?? "";

      let updatedContent = editorContent;

      const base64Regex = /<img[^>]+src="(data:image\/[^;]+;base64,[^"]+)"[^>]*>/g;
      const matches = [...editorContent.matchAll(base64Regex)];

      for (const match of matches) {
        const base64Data = match[1];
        if (!base64Data) continue;

        const blob = await fetch(base64Data).then((res) => res.blob());
        const file = new File([blob], "upload.png", { type: blob.type });
        const uploadRes = await uploadCallback(file);

        const uploadedUrl =
          uploadRes?.data?.url ||
          uploadRes?.data?.data?.url ||
          uploadRes?.data?.path ||
          uploadRes?.data;

        if (typeof uploadedUrl === "string") {
          updatedContent = updatedContent.replace(base64Data, uploadedUrl);
        }
      }

      const token = getToken();

      const isEditingExistingPrivateTemplate =
        !!editTemplate.id &&
        practiceTemplates.some((t) => t.id === editTemplate.id);

      if (isEditingExistingPrivateTemplate) {
        const payload = {
          name: editTemplate.name,
          body: updatedContent,
          level: selectedLevel,
          is_default: editTemplate.is_default ?? false,
          is_cloned: editTemplate.is_cloned ?? false,
          parent_id: editTemplate.parent_id ?? null,
          treatment_id: selectedTreatments.length > 0 ? selectedTreatments[0] : null,
          treatment_ids: selectedTreatments,
          category_id: editTemplate.category_id ?? null,
        };

        await axios.put(
          `${process.env.REACT_APP_BASE_URL}consent_clause/${editTemplate.id}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else if (builderTab === "existing" && editTemplate.id) {
        const payload = {
          name: editTemplate.name,
          body: updatedContent,
          level: selectedLevel,
          is_cloned: false,
          is_default: false,
          parent_id: null,
          treatment_id: selectedTreatments.length > 0 ? selectedTreatments[0] : null,
          treatment_ids: selectedTreatments,
        };

        await axios.put(
          `${process.env.REACT_APP_BASE_URL}consent_clause/${editTemplate.id}/copy_global_template_to_private`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        const payload = {
          name: editTemplate.name,
          body: updatedContent,
          level: selectedLevel,
          is_default: false,
          is_cloned: false,
          parent_id: null,
          treatment_id: selectedTreatments.length > 0 ? selectedTreatments[0] : null,
          treatment_ids: selectedTreatments,
          category_id: null,
        };

        await axios.post(`${process.env.REACT_APP_BASE_URL}consent_clause`, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      toast.success("Template saved successfully");
      setEditTemplate(null);
      setMode("empty");
      setBuilderTab("existing");
      setSelectedTreatments([]);
      setTreatmentSearch("");
      setGeneratorPrompt("");
      await fetchPracticeTemplates();
    } catch (err: any) {
      console.error(err);

      const backendMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.title ||
        "Failed to save template";

      toast.error(
        typeof backendMessage === "string"
          ? backendMessage
          : JSON.stringify(backendMessage)
      );
    }
  };

  const handleNext = () => navigate("/previewComplete");
  const handleBack = () => navigate("/portfolioGallery");

  const handlePreviewFromEditor = () => {
    if (!editTemplate) return;

    const editorContent =
      window.CKEDITOR?.instances?.editor1?.getData?.() || editTemplate.body;

    setPreview({
      ...editTemplate,
      body: editorContent,
    });
  };

  const filteredTreatments = treatments.filter((t) => {
    const label = getTreatmentLabel(t).toLowerCase();
    return (
      label.includes(treatmentSearch.toLowerCase()) &&
      !selectedTreatments.includes(t.id)
    );
  });

  const confirmDeleteTemplate = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      const token = getToken();

      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}consent_clause/${deleteTarget.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Template deleted successfully");
      setDeleteTarget(null);
      await fetchPracticeTemplates();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete template");
    } finally {
      setDeleting(false);
    }
  };

  const generatorWordCount = countWords(generatorPrompt);
  const isGeneratorPromptTooLong = generatorWordCount > 20;

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.main}>
        <SetupSteps
          steps={STEPS}
          current={5}
          rightLabel="80% Complete"
          onStepClick={(step) => navigate(step.path)}
        />

        {mode === "empty" ? (
          <>
            <h1 className={styles.title}>Consent Templates</h1>
            <p className={styles.subtitle}>
              Create consent templates for your most common procedures.
            </p>

            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <h2>Your Templates ({practiceTemplates.length})</h2>
                <button
                  className={styles.createBtnSmall}
                  onClick={() => {
                    setMode("builder");
                    setBuilderTab("existing");
                    setEditTemplate(null);
                    setSelectedTreatments([]);
                    setTreatmentSearch("");
                    setGeneratorPrompt("");
                  }}
                >
                  + Create New Template
                </button>
              </div>

              {loading ? (
                <div className={styles.loading}>Loading...</div>
              ) : (
                <div className={styles.practiceTemplatesList}>
                  {practiceTemplates.map((t) => (
                    <div key={t.id} className={styles.practiceTemplateItem}>
                      <div>{t.name}</div>

                      <div className={styles.actions}>
                        <button className={styles.viewBtn} onClick={() => setPreview(t)}>
                          View
                        </button>

                        <button
                          className={styles.ediBtn}
                          onClick={() => handleEditPracticeTemplate(t)}
                        >
                          Edit
                        </button>

                        <button
                          className={styles.delBtn}
                          onClick={() => setDeleteTarget(t)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        ) : (
          <>
            <div className={styles.builderHeader}>
              <h1>Create Consent Template</h1>

              <button
                className={styles.backToListBtn}
                onClick={() => {
                  setMode("empty");
                  setBuilderTab("existing");
                  setEditTemplate(null);
                  setGeneratorPrompt("");
                }}
              >
                ← Back
              </button>
            </div>

            <section className={styles.card}>
              <div className={styles.builderTabs}>
                <button
                  type="button"
                  className={`${styles.builderTabBtn} ${
                    builderTab === "existing" ? styles.activeTab : ""
                  }`}
                  onClick={() => {
                    setBuilderTab("existing");
                    setEditTemplate(null);
                    setSelectedTreatments([]);
                    setTreatmentSearch("");
                    setGeneratorPrompt("");
                  }}
                >
                  Use Global Template
                </button>

                <button
  type="button"
  className={`${styles.builderTabBtn} ${
    builderTab === "generate" ? styles.activeTab : ""
  }`}
  onClick={() => {
    setBuilderTab("generate");
    setEditTemplate(null);
    setSelectedTreatments([]);
    setTreatmentSearch("");
  }}
>
  <span className={styles.generateBtnInner}>
    <HiSparkles className={styles.aiIcon} />
    Generate Template
  </span>
</button>
              </div>

              {builderTab === "existing" ? (
                <>
                  <div className={styles.builderTabIntro}>
                    Select a global consent template and customize it for your practice.
                  </div>

                  {loading ? (
                    <div className={styles.loading}>Loading...</div>
                  ) : (
                    <div className={styles.templatesGrid}>
                      {templates.map((t) => (
                        <div
                          key={t.id}
                          className={styles.templateItem}
                          onClick={() => openGlobalTemplateEditor(t)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              openGlobalTemplateEditor(t);
                            }
                          }}
                        >
                          <div className={styles.templateName}>{t.name}</div>
                          <FiEdit2 className={styles.editIcon} />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.generateTemplateSection}>
  <div className={styles.generateHero}>
    <div className={styles.generateHeroContent}>
      <h2>Generate a New Consent Template</h2>
      <p>
        Describe the treatment in a short prompt and we will generate an editable
        consent template for you. You can then rename it, assign treatments,
        and update the content in the editor.
      </p>
    </div>

    <div className={styles.generateHeroBadge}>
      <span>AI Assisted</span>
    </div>
  </div>

  <div className={styles.generateLayout}>
    <div className={styles.generateMainCard}>
      <div className={styles.generateCardHeader}>
        <h3>Treatment Prompt</h3>
        <p>Keep the prompt short and specific. Maximum 20 words.</p>
      </div>

      <div className={styles.generateInputWrap}>
        <textarea
          className={styles.generateTextarea}
          rows={5}
          value={generatorPrompt}
          onChange={(e) => setGeneratorPrompt(e.target.value)}
          placeholder="Example: Composite bonding for chipped front tooth repair"
          disabled={generatingTemplate}
        />

        <div className={styles.generateMetaRow}>
          <div
            className={`${styles.wordCount} ${
              isGeneratorPromptTooLong ? styles.wordCountError : ""
            }`}
          >
            {generatorWordCount}/20 words
          </div>

          <div className={styles.generateHint}>
            A short treatment-focused phrase works best.
          </div>
        </div>
      </div>

      <div className={styles.generateActions}>
        <button
          className={styles.generateBtn}
          onClick={handleGenerateTemplate}
          disabled={generatingTemplate || isGeneratorPromptTooLong || !generatorPrompt.trim()}
        >
          {generatingTemplate ? (
            <span className={styles.generateBtnInner}>
              <span className={styles.buttonSpinner} />
              Generating Template...
            </span>
          ) : (
            <span className={styles.generateBtnInner}>
              <HiSparkles className={styles.aiIcon} />
              Generate Template
            </span>
          )}
        </button>
      </div>
    </div>

    <div className={styles.generateSideCard}>
      <h3>Prompt Tips</h3>

      <div className={styles.tipList}>
        <div className={styles.tipItem}>
          <strong>Be specific</strong>
          <span>Include the treatment name and short context.</span>
        </div>

        <div className={styles.tipItem}>
          <strong>Keep it short</strong>
          <span>Do not exceed 20 words.</span>
        </div>

        <div className={styles.tipItem}>
          <strong>Use plain language</strong>
          <span>Avoid combining too many procedures in one prompt.</span>
        </div>
      </div>

      <div className={styles.exampleBlock}>
        <div className={styles.exampleTitle}>Examples</div>

        <button
          type="button"
          className={styles.examplePrompt}
          onClick={() =>
            !generatingTemplate &&
            setGeneratorPrompt("Composite bonding for chipped upper front tooth")
          }
        >
          Composite bonding for chipped upper front tooth
        </button>

        <button
          type="button"
          className={styles.examplePrompt}
          onClick={() =>
            !generatingTemplate &&
            setGeneratorPrompt("Root canal treatment for lower molar with crown advice")
          }
        >
          Root canal treatment for lower molar with crown advice
        </button>

        <button
          type="button"
          className={styles.examplePrompt}
          onClick={() =>
            !generatingTemplate &&
            setGeneratorPrompt("Simple extraction for painful broken tooth")
          }
        >
          Simple extraction for painful broken tooth
        </button>
      </div>
    </div>
  </div>

  {generatingTemplate && (
    <div className={styles.generateLoadingOverlay}>
      <div className={styles.generateLoadingCard}>
        <div className={styles.generateSpinner} />
        <h3>Generating template...</h3>
        <p>Analysing your prompt and building an editable consent draft.</p>
      </div>
    </div>
  )}
</div>
              )}
            </section>
          </>
        )}

        {deleteTarget && (
          <div className={styles.modalOverlay} onClick={() => setDeleteTarget(null)}>
            <div
              className={styles.confirmModal}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h3>Delete Template</h3>
                <button
                  className={styles.closeBtn}
                  onClick={() => setDeleteTarget(null)}
                >
                  ✕
                </button>
              </div>

              <div className={styles.modalBody}>
                <p>
                  Are you sure you want to delete
                  <strong> “{deleteTarget.name}”</strong>?
                </p>
                <p className={styles.warningText}>This action cannot be undone.</p>
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>

                <button
                  className={styles.deleteConfirmBtn}
                  onClick={confirmDeleteTemplate}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={styles.nextWrapper}>
          <button className={styles.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <button className={styles.nextBtn} onClick={handleNext}>
            Next →
          </button>
        </div>
      </main>

      {editTemplate && (
        <div className={styles.modalOverlay} onClick={() => setEditTemplate(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>
                {builderTab === "existing" && editTemplate.id
                  ? "Edit Global Template"
                  : "Generated Template"}
              </h2>
              <button className={styles.closeBtn} onClick={() => setEditTemplate(null)}>
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  type="text"
                  value={editTemplate.name}
                  onChange={(e) =>
                    setEditTemplate({ ...editTemplate, name: e.target.value })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label>Select Treatments</label>

                <div className={styles.multiSelectBox}>
                  <div className={styles.selectedChips}>
                    {selectedTreatments.map((id) => {
                      const treatment = treatments.find((t) => t.id === id);
                      return (
                        <div key={id} className={styles.chip}>
                          {treatment ? getTreatmentLabel(treatment) : `Treatment ${id}`}
                          <span
                            className={styles.removeChip}
                            onClick={() =>
                              setSelectedTreatments((prev) =>
                                prev.filter((x) => x !== id)
                              )
                            }
                          >
                            ✕
                          </span>
                        </div>
                      );
                    })}

                    <div className={styles.searchSelectWrapper}>
                      <input
                        type="text"
                        placeholder="Search treatments..."
                        className={styles.treatmentSearchInput}
                        value={treatmentSearch}
                        onChange={(e) => {
                          setTreatmentSearch(e.target.value);
                          setDropdownOpen(true);
                        }}
                        onFocus={() => setDropdownOpen(true)}
                      />

                      {dropdownOpen && filteredTreatments.length > 0 && (
                        <div className={styles.dropdownList}>
                          {filteredTreatments.map((t) => (
                            <div
                              key={t.id}
                              className={styles.dropdownItem}
                              onClick={() => {
                                setSelectedTreatments((prev) => [...prev, t.id]);
                                setTreatmentSearch("");
                                setDropdownOpen(false);
                              }}
                            >
                              {getTreatmentLabel(t)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <textarea
                id="editor1"
                ref={editorRef}
                className={styles.editorTextarea}
                defaultValue={editTemplate.body}
              />
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.viewBtnOutlined} onClick={handlePreviewFromEditor}>
                Preview
              </button>

              <button className={styles.saveBtn} onClick={handleSave}>
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {showGallery && (
        <div className={styles.galleryOverlay} onClick={() => setShowGallery(false)}>
          <div className={styles.galleryPanel} onClick={(e) => e.stopPropagation()}>
            <ImageGalleryV2
              showGallery={showGallery}
              onClose={() => setShowGallery(false)}
              onSelectImage={(url: string) => {
                const editor = window.CKEDITOR?.instances?.[activeEditorId || "editor1"];
                if (!editor) return;
                editor.insertHtml(`<img src="${url}" alt="" />`);
                setShowGallery(false);
              }}
            />
          </div>
        </div>
      )}

      {preview && (
        <div className={styles.modalOverlay} onClick={() => setPreview(null)}>
          <div
            className={`${styles.modalContent} ${styles.previewModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>Preview: {preview.name}</h2>
              <button className={styles.closeBtn} onClick={() => setPreview(null)}>
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              <div
                className={styles.previewBody}
                dangerouslySetInnerHTML={{ __html: preview.body }}
              />
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.closeBtnOutlined}
                onClick={() => setPreview(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}